/**
 * Checkout Wizard Mockup
 * Interactive prototype for stakeholder presentation
 * All data is mocked - no API calls
 */

import React, { useState } from 'react';
import './checkout-wizard-mockup.scss';
import {
  mockStoreDetails,
  mockAddresses,
  mockTimeSlots,
  mockPriceList,
  mockPickupPriceList,
  mockCartInfo,
  mockCoupons,
  mockEbtPaidList,
  mockEbtPaidListEmpty,
  mockUser,
  mockDoorDashOptions,
  mockTipOptions,
  mockContactTypes,
  mockPaymentMethods,
  wizardSteps,
  initialFormState,
} from './mockData';

const CheckoutWizardMockup = () => {
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState(initialFormState);
  const [showEbtPayments, setShowEbtPayments] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState(['SAVE10']);

  // Derived state
  const isDelivery = formState.deliveryMethod === 'delivery';
  const isDoorDash = isDelivery && mockStoreDetails.third_party_delivery_option === 'door_dash';
  const priceList = isDelivery ? mockPriceList : mockPickupPriceList;
  const paymentMethods = isDelivery 
    ? mockPaymentMethods.grocery.delivery 
    : mockPaymentMethods.grocery.pickup;

  // Calculate step completion (computed directly, no memoization to avoid stale values)
  const isStep1Complete = (() => {
    if (!formState.selectedTimeSlot) return false;
    // Only require DoorDash confirmation when in delivery mode with DoorDash
    if (isDelivery && mockStoreDetails.third_party_delivery_option === 'door_dash') {
      if (!formState.doorDashConfirmed) return false;
    }
    return true;
  })();

  const isStep2Complete = (() => {
    if (!formState.contactNumber || formState.contactNumber.length < 10) return false;
    if (isDelivery && !formState.selectedDeliveryAddress) return false;
    return true;
  })();

  const isStep3Complete = (() => {
    if (!formState.selectedPaymentMethod) return false;
    if (formState.selectedPaymentMethod === 'cc' && !formState.selectedBillingAddress) return false;
    if (mockCartInfo.age_restricted_notes && !formState.ageRestrictionAccepted) return false;
    return true;
  })();

  // Handlers
  const updateForm = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  // For mockup: allow free navigation between all steps (no validation)
  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleCouponToggle = (couponValue) => {
    setSelectedCoupons(prev => 
      prev.includes(couponValue)
        ? prev.filter(c => c !== couponValue)
        : [...prev, couponValue]
    );
  };

  // For mockup: show steps as completed if they're before current step
  const getStepStatus = (stepId) => {
    if (stepId === currentStep) return 'active';
    if (stepId < currentStep) return 'completed';
    return 'disabled';
  };

  const getProgressWidth = () => {
    if (currentStep === 1) return '0%';
    if (currentStep === 2) return '50%';
    return '100%';
  };

  return (
    <div className="checkout-wizard-mockup">
      {/* Header */}
      {/* Header with Integrated Progress */}
      <header className="wizard-header">
        <div className="wizard-header__container">
          <div className="wizard-header__row">
            <h1 className="wizard-header__title">
              Checkout
            </h1>
            
            {/* Progress Indicator (Moved straight into header) */}
            <nav className="wizard-progress">
              <div className="wizard-progress__steps">
                {wizardSteps.map((step, index) => {
                  const status = getStepStatus(step.id);
                  const getStepIcon = () => {
                    if (status === 'completed') return '✓';
                    if (step.icon === 'fa-truck') return '🚚';
                    if (step.icon === 'fa-location-dot') return '📍';
                    if (step.icon === 'fa-credit-card') return '💳';
                    return step.id;
                  };
                  
                  return (
                    <React.Fragment key={step.id}>
                      <div
                        className={`wizard-progress__step wizard-progress__step--${status}`}
                        onClick={() => handleStepClick(step.id)}
                      >
                        <div className="wizard-progress__step-circle">
                          {getStepIcon()}
                        </div>
                        <span className="wizard-progress__step-title">{step.title}</span>
                      </div>
                      
                      {/* Connector line between steps */}
                      {index < wizardSteps.length - 1 && (
                        <div className={`wizard-progress__connector ${index < currentStep - 1 ? 'wizard-progress__connector--filled' : ''}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="wizard-layout">
        {/* Step Content */}
        <main>
          <div className="wizard-step-wrapper">
            {currentStep === 1 && (
              <Step1DeliveryScheduling
                formState={formState}
                updateForm={updateForm}
                mockStoreDetails={mockStoreDetails}
                mockTimeSlots={mockTimeSlots}
                mockDoorDashOptions={mockDoorDashOptions}
                mockTipOptions={mockTipOptions}
                mockCartInfo={mockCartInfo}
                isDelivery={isDelivery}
                isDoorDash={isDoorDash}
              />
            )}
            {currentStep === 2 && (
              <Step2ContactAddress
                formState={formState}
                updateForm={updateForm}
                mockAddresses={mockAddresses}
                mockContactTypes={mockContactTypes}
                mockStoreDetails={mockStoreDetails}
                mockUser={mockUser}
                isDelivery={isDelivery}
              />
            )}
            {currentStep === 3 && (
              <Step3ReviewPayment
                formState={formState}
                updateForm={updateForm}
                mockStoreDetails={mockStoreDetails}
                mockAddresses={mockAddresses}
                mockCartInfo={mockCartInfo}
                paymentMethods={paymentMethods}
                showEbtPayments={showEbtPayments}
                mockEbtPaidList={showEbtPayments ? mockEbtPaidList : mockEbtPaidListEmpty}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="wizard-navigation">
            <button
              type="button"
              className="wizard-navigation__btn wizard-navigation__btn--back"
              onClick={handleBack}
              style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
            >
              ← Back
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                className="wizard-navigation__btn wizard-navigation__btn--next"
                onClick={handleNext}
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                className="wizard-navigation__btn wizard-navigation__btn--place-order"
              >
                🔒 Place Order • ${priceList.find(p => p.key_name === 'grand_total')?.amount}
              </button>
            )}
          </div>
        </main>

        {/* Order Summary Sidebar */}
        <aside>
          <OrderSummary
            priceList={priceList}
            mockStoreDetails={mockStoreDetails}
            mockCartInfo={mockCartInfo}
            mockCoupons={mockCoupons}
            selectedCoupons={selectedCoupons}
            onCouponToggle={handleCouponToggle}
            isDelivery={isDelivery}
          />
        </aside>
      </div>
    </div>
  );
};

// Step 1: Delivery & Scheduling
const Step1DeliveryScheduling = ({
  formState,
  updateForm,
  mockStoreDetails,
  mockTimeSlots,
  mockDoorDashOptions,
  mockTipOptions,
  mockCartInfo,
  isDelivery,
  isDoorDash,
}) => {
  const showDoorDash = isDoorDash && isDelivery;
  const showCurbside = !isDelivery && mockCartInfo.is_curbside_pickup === "1";

  return (
    <div className="wizard-step">
      <h2 className="wizard-step__title">
        🚚 Delivery & Scheduling
      </h2>
      <p className="wizard-step__description">
        Choose how you'd like to receive your order and select a convenient time
      </p>

      {/* Delivery Method - Horizontal Cards */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          📍 How would you like to receive your order?
        </h3>

        <div className="delivery-method-cards">
          <div
            className={`delivery-method-card ${!isDelivery ? 'delivery-method-card--selected' : ''}`}
            onClick={() => updateForm('deliveryMethod', 'store_pickup')}
            role="button"
            tabIndex={0}
          >
            <div className="delivery-method-card__icon">🏪</div>
            <div className="delivery-method-card__content">
              <div className="delivery-method-card__title">Store Pickup</div>
              <div className="delivery-method-card__subtitle">Pick up at store</div>
            </div>
            {!isDelivery && <div className="delivery-method-card__check">✓</div>}
          </div>

          <div
            className={`delivery-method-card ${isDelivery ? 'delivery-method-card--selected' : ''}`}
            onClick={() => updateForm('deliveryMethod', 'delivery')}
            role="button"
            tabIndex={0}
          >
            <div className="delivery-method-card__icon">🚚</div>
            <div className="delivery-method-card__content">
              <div className="delivery-method-card__title">
                {mockStoreDetails.delivery_method_lable}
              </div>
              <div className="delivery-method-card__subtitle">
                {mockStoreDetails.third_party_delivery_option === 'door_dash' ? 'Via DoorDash' : 'To your address'}
              </div>
            </div>
            {isDelivery && <div className="delivery-method-card__check">✓</div>}
          </div>
        </div>
      </div>

      {/* DoorDash Section */}
      {showDoorDash && (
        <div className="doordash-section">
          <h3 className="doordash-section__title">
            🏍️ DoorDash Delivery Options
          </h3>

          <div className="form-group">
            <label className="form-group__label">Drop-off preference</label>
            <select
              className="form-select"
              value={formState.doorDashContactless}
              onChange={(e) => updateForm('doorDashContactless', e.target.value)}
            >
              {mockDoorDashOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-group__label">Drop-off instructions (optional)</label>
            <textarea
              className="form-textarea"
              placeholder="E.g., Ring doorbell, leave on porch, etc."
              value={formState.dropOffInstructions}
              onChange={(e) => updateForm('dropOffInstructions', e.target.value)}
            />
          </div>

          {mockStoreDetails.allow_tip_amount === "1" && (
            <div className="form-group">
              <label className="form-group__label">Driver Tip</label>
              <div className="tip-buttons">
                {mockTipOptions.map(tip => (
                  <button
                    key={tip.value}
                    type="button"
                    className={`tip-buttons__btn ${formState.tipAmount === tip.value ? 'tip-buttons__btn--selected' : ''}`}
                    onClick={() => updateForm('tipAmount', tip.value)}
                  >
                    {tip.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="doordash-section__confirm">
            <div
              className={`toggle-card ${formState.doorDashConfirmed ? 'toggle-card--selected' : ''}`}
              onClick={() => updateForm('doorDashConfirmed', !formState.doorDashConfirmed)}
              role="button"
              tabIndex={0}
              style={{ padding: '0.75rem', marginBottom: 0 }}
            >
              <div className="toggle-card__icon" style={{ width: '36px', height: '36px', fontSize: '1rem' }}>📍</div>
              <div className="toggle-card__content">
                <div className="toggle-card__title" style={{ fontSize: '0.9rem' }}>Confirm Delivery Availability</div>
                <div className="toggle-card__subtitle" style={{ fontSize: '0.8rem' }}>
                  I confirm that DoorDash can deliver to my address
                </div>
              </div>
              <div className="toggle-card__toggle">
                <div className={`toggle-switch ${formState.doorDashConfirmed ? 'toggle-switch--on' : ''}`}>
                  <div className="toggle-switch__slider" />
                </div>
              </div>
            </div>
            {!formState.doorDashConfirmed && isDelivery && (
              <p className="form-group__error" style={{ marginTop: '0.5rem', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                * Required to continue
              </p>
            )}
          </div>
        </div>
      )}

      {/* Time Slot Selection - Cards instead of dropdown */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          🕐 Select a {isDelivery ? 'delivery' : 'pickup'} time
        </h3>

        <div className="timeslot-cards">
          {mockTimeSlots.map(slot => (
            <div
              key={slot.title}
              className={`timeslot-card ${formState.selectedTimeSlot?.title === slot.title ? 'timeslot-card--selected' : ''}`}
              onClick={() => updateForm('selectedTimeSlot', slot)}
              role="button"
              tabIndex={0}
            >
              <div className="timeslot-card__date">{slot.label}</div>
              <div className="timeslot-card__time">{slot.time}</div>
              {formState.selectedTimeSlot?.title === slot.title && (
                <div className="timeslot-card__check">✓</div>
              )}
            </div>
          ))}
        </div>
        
        {!formState.selectedTimeSlot && (
          <p className="form-group__error" style={{ marginTop: '1rem' }}>
            ⚠️ Please select a time slot to continue
          </p>
        )}
      </div>

      {/* Curbside Pickup - Clickable Card */}
      {showCurbside && (
        <div className="wizard-section">
          <h3 className="wizard-section__title">
            🚗 Pickup Options
          </h3>

          <div
            className={`toggle-card ${formState.isCurbsidePickup ? 'toggle-card--selected' : ''}`}
            onClick={() => updateForm('isCurbsidePickup', !formState.isCurbsidePickup)}
            role="button"
            tabIndex={0}
          >
            <div className="toggle-card__icon">🚗</div>
            <div className="toggle-card__content">
              <div className="toggle-card__title">Curbside Pickup</div>
              <div className="toggle-card__subtitle">
                Stay in your car and we'll bring your order to you
              </div>
            </div>
            <div className="toggle-card__toggle">
              <div className={`toggle-switch ${formState.isCurbsidePickup ? 'toggle-switch--on' : ''}`}>
                <div className="toggle-switch__slider" />
              </div>
            </div>
          </div>

          {formState.isCurbsidePickup && (
            <div className="curbside-details">
              <div className="form-group">
                <label className="form-group__label">
                  🚗 Vehicle Details
                </label>
                <textarea
                  className="form-textarea"
                  placeholder="E.g., Red Toyota Camry, License: ABC123"
                  value={formState.curbsideComment}
                  onChange={(e) => updateForm('curbsideComment', e.target.value)}
                />
                <p className="form-group__hint">
                  ℹ️ {mockCartInfo.curbside_instruction_notes}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Step 2: Contact & Address
const Step2ContactAddress = ({
  formState,
  updateForm,
  mockAddresses,
  mockContactTypes,
  mockStoreDetails,
  mockUser,
  isDelivery,
}) => {
  return (
    <div className="wizard-step">
      <h2 className="wizard-step__title">
        📍 Contact & Address
      </h2>
      <p className="wizard-step__description">
        Provide your contact information and {isDelivery ? 'delivery address' : 'confirm pickup location'}
      </p>

      {/* Contact Information */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          📞 Enter your Number
        </h3>
        
        <p className="wizard-section__description" style={{ marginBottom: '1rem', color: 'var(--wizard-text-muted)', fontSize: '0.9rem' }}>
          If required store will contact you about substitutions or another questions about the order. Choose your preferred contact method.
        </p>

        <div className="contact-grid">
          <div className="form-group">
            <label className="form-group__label">Contact me by</label>
            <select
              className="form-select"
              value={formState.contactType}
              onChange={(e) => updateForm('contactType', e.target.value)}
            >
              {mockContactTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-group__label">Phone Number</label>
            <input
              type="tel"
              className={`form-input ${formState.contactNumber.length < 10 ? 'form-input--error' : ''}`}
              placeholder="(555) 123-4567"
              value={formState.contactNumber}
              onChange={(e) => updateForm('contactNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
            />
            {formState.contactNumber.length < 10 && (
              <p className="form-group__error">
                ⚠️ Please enter a valid 10-digit phone number
              </p>
            )}
          </div>
        </div>
        
        <div className="disclaimer-text" style={{ 
          marginTop: '1rem', 
          fontSize: '0.8rem', 
          color: 'var(--wizard-text-muted)',
          padding: '0.75rem',
          background: 'var(--wizard-bg-muted)',
          borderRadius: 'var(--wizard-radius)',
          lineHeight: '1.4'
        }}>
          <p style={{ margin: '0 0 0.5rem 0' }}>* Your final amount could vary after processing the order due to variable weights, substitutions, taxes and add-ons.</p>
          <p style={{ margin: 0 }}>* Prices are subject to change for future orders</p>
        </div>
      </div>

      {/* Order Notes */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          ✏️ Order Notes (Optional)
        </h3>

        <div className="form-group">
          <textarea
            className="form-textarea"
            placeholder="Any special instructions for your order..."
            value={formState.orderNotes}
            onChange={(e) => updateForm('orderNotes', e.target.value)}
          />
        </div>
      </div>

      {/* Delivery Address or Pickup Location */}
      {isDelivery ? (
        <div className="wizard-section">
          <h3 className="wizard-section__title">
            📬 Delivery Address
          </h3>

          <div className="option-cards">
            {mockAddresses.shipping.map(addr => (
              <div
                key={addr.id}
                className={`address-card ${formState.selectedDeliveryAddress === addr.id ? 'option-card--selected' : ''}`}
                onClick={() => updateForm('selectedDeliveryAddress', addr.id)}
                role="button"
                tabIndex={0}
              >
                <div className="option-card__radio" />
                <div className="option-card__content">
                  <div className="option-card__title">
                    {addr.first_name} {addr.last_name}
                  </div>
                  <div className="address-card__details">
                    {addr.address_1}{addr.address_2 && `, ${addr.address_2}`}<br />
                    {addr.city}, {addr.state} {addr.zip_code}
                  </div>
                  {addr.is_primary === "1" && (
                    <span className="address-card__primary">
                      ⭐ Default
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="add-new-btn">
            + Add New Address
          </button>

          {!formState.selectedDeliveryAddress && (
            <p className="form-group__error" style={{ marginTop: '0.5rem' }}>
              ⚠️ Please select a delivery address
            </p>
          )}
        </div>
      ) : (
        <div className="wizard-section">
          <h3 className="wizard-section__title">
            🏪 Pickup Location
          </h3>

          <div className="pickup-location">
            <div className="pickup-location__icon">🏪</div>
            <div className="pickup-location__details">
              <h4>{mockStoreDetails.ClientStoreName}</h4>
              <p>
                {mockStoreDetails.store_address}<br />
                {mockStoreDetails.store_city}, {mockStoreDetails.store_state} {mockStoreDetails.store_zip}<br />
                📞 {mockStoreDetails.store_phone}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 3: Review & Payment
const Step3ReviewPayment = ({
  formState,
  updateForm,
  mockStoreDetails,
  mockAddresses,
  mockCartInfo,
  paymentMethods,
  showEbtPayments,
  mockEbtPaidList,
}) => {
  return (
    <div className="wizard-step">
      <h2 className="wizard-step__title">
        💳 Review & Payment
      </h2>
      <p className="wizard-step__description">
        Review your order details and complete payment
      </p>

      {/* Options Section */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          ⚙️ Order Options
        </h3>

        {/* Tax Exempt */}
        {mockStoreDetails.is_approved_tax_exempt === "1" && (
          <div
            className={`toggle-card ${formState.isTaxExempt ? 'toggle-card--selected' : ''}`}
            onClick={() => updateForm('isTaxExempt', !formState.isTaxExempt)}
            role="button"
            tabIndex={0}
          >
            <div className="toggle-card__icon">🧾</div>
            <div className="toggle-card__content">
              <div className="toggle-card__title">Tax Exemption</div>
              <div className="toggle-card__subtitle">
                Apply tax exemption to this order
              </div>
            </div>
            <div className="toggle-card__toggle">
              <div className={`toggle-switch ${formState.isTaxExempt ? 'toggle-switch--on' : ''}`}>
                <div className="toggle-switch__slider" />
              </div>
            </div>
          </div>
        )}

        {/* Age Restriction - Smaller Warning Card */}
        {mockCartInfo.age_restricted_notes && (
          <div
            className={`toggle-card toggle-card--warning ${formState.ageRestrictionAccepted ? 'toggle-card--selected' : ''}`}
            onClick={() => updateForm('ageRestrictionAccepted', !formState.ageRestrictionAccepted)}
            role="button"
            tabIndex={0}
          >
            <div className="toggle-card__icon">🔞</div>
            <div className="toggle-card__content">
              <div className="toggle-card__title">Age Verification Required</div>
              <div className="toggle-card__subtitle">
                I confirm I am 21+ years old
              </div>
            </div>
            <div className="toggle-card__toggle">
              <div className={`toggle-switch ${formState.ageRestrictionAccepted ? 'toggle-switch--on' : ''}`}>
                <div className="toggle-switch__slider" />
              </div>
            </div>
          </div>
        )}

        {/* Printed Receipt */}
        {mockStoreDetails.include_printed_receipt_with_order === "1" && (
          <div
            className={`toggle-card ${formState.includePrintedReceipt ? 'toggle-card--selected' : ''}`}
            onClick={() => updateForm('includePrintedReceipt', !formState.includePrintedReceipt)}
            role="button"
            tabIndex={0}
          >
            <div className="toggle-card__icon">📄</div>
            <div className="toggle-card__content">
              <div className="toggle-card__title">Printed Receipt</div>
              <div className="toggle-card__subtitle">
                Include a physical receipt with my order
              </div>
            </div>
            <div className="toggle-card__toggle">
              <div className={`toggle-switch ${formState.includePrintedReceipt ? 'toggle-switch--on' : ''}`}>
                <div className="toggle-switch__slider" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* EBT Paid List */}
      {mockEbtPaidList.length > 0 && (
        <div className="ebt-paid-list">
          <h3 className="ebt-paid-list__title">
            ✅ EBT Payment Applied
          </h3>

          {mockEbtPaidList.map((payment, idx) => (
            <div key={idx} className="ebt-paid-list__item">
              <div className="ebt-paid-list__details">
                Card ending in <strong>{payment.card_number}</strong>
                <br />
                <span>Remaining balance: ${payment.balance}</span>
              </div>
              <div className="ebt-paid-list__amount">
                -${payment.amount}
              </div>
              <button type="button" className="ebt-paid-list__cancel">
                ✕ Cancel
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Payment Methods */}
      <div className="wizard-section">
        <h3 className="wizard-section__title">
          💳 Payment Method
        </h3>

        {/* EBT SNAP Warning */}
        <div className="ebt-note" style={{ 
          marginTop: '-0.5rem',
          marginBottom: '1rem', 
          fontSize: '0.8rem', 
          color: 'var(--wizard-text-muted)',
          padding: '0.75rem',
          background: 'var(--wizard-bg-muted)',
          borderRadius: 'var(--wizard-radius)',
          borderLeft: '3px solid #16a34a'
        }}>
          <strong style={{ display: 'block', marginBottom: '0.25rem', color: '#16a34a' }}>EBT SNAP</strong>
          <p style={{ margin: 0, lineHeight: '1.4' }}>
            * Items sold by weight that are paid using EBT SNAP will have 10% of the cost of the weighted item added to the order to cover weight variances. This amount will be refunded if it is over the final amount provided when the order is fulfilled.
          </p>
        </div>

        <div className="option-cards">
          {paymentMethods.map(method => (
            <React.Fragment key={method.value}>
              <div
                className={`payment-card ${formState.selectedPaymentMethod === method.value ? 'option-card--selected' : ''}`}
                onClick={() => updateForm('selectedPaymentMethod', method.value)}
                role="button"
                tabIndex={0}
              >
                <div className="option-card__radio" />
                <div className="option-card__icon">
                  {method.value === 'cc' && '💳'}
                  {method.value === 'cod' && '💵'}
                  {method.value === 'ebt' && '🪪'}
                  {method.value === 'house_charge' && '🏢'}
                </div>
                <div className="option-card__content">
                  <div className="option-card__title">{method.label}</div>
                  {method.balance && (
                    <div className="payment-card__balance">
                      Available balance: ${method.balance}
                    </div>
                  )}
                </div>
              </div>

              {/* Nested Billing Address for CC */}
              {method.value === 'cc' && formState.selectedPaymentMethod === 'cc' && (
                <div className="nested-section" style={{ 
                  marginLeft: '2rem', 
                  marginTop: '-0.5rem', 
                  marginBottom: '1rem',
                  paddingLeft: '1rem',
                  borderLeft: '2px solid var(--wizard-border)',
                  animation: 'fadeSlideIn 0.3s ease'
                }}>
                  <h4 className="wizard-section__subtitle" style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--wizard-text-muted)' }}>
                    Billing Address
                  </h4>
                  
                  <div className="option-cards" style={{ gap: '0.5rem' }}>
                    {mockAddresses.billing.map(addr => (
                      <div
                        key={addr.id}
                        className={`address-card ${formState.selectedBillingAddress === addr.id ? 'option-card--selected' : ''}`}
                        onClick={() => updateForm('selectedBillingAddress', addr.id)}
                        role="button"
                        tabIndex={0}
                        style={{ padding: '0.75rem' }}
                      >
                        <div className="option-card__radio" />
                        <div className="option-card__content">
                          <div className="option-card__title" style={{ fontSize: '0.9rem' }}>
                            {addr.first_name} {addr.last_name}
                          </div>
                          <div className="address-card__details" style={{ fontSize: '0.8rem' }}>
                            {addr.address_1}, {addr.city} {addr.zip_code}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button type="button" className="add-new-btn" style={{ marginTop: '0.5rem', padding: '0.5rem', fontSize: '0.8rem' }}>
                    + Add New Billing Address
                  </button>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// Order Summary Sidebar
const OrderSummary = ({
  priceList,
  mockStoreDetails,
  mockCartInfo,
  mockCoupons,
  selectedCoupons,
  onCouponToggle,
  isDelivery,
}) => {
  return (
    <div className="order-summary">
      <div className="order-summary__header">
        <h3 className="order-summary__title">Order Summary</h3>
        <a href="#" className="order-summary__edit-link">
          ✏️ Edit Cart
        </a>
      </div>

      <div className="order-summary__store">
        <span>🏪 {mockStoreDetails.ClientStoreName}</span>
      </div>

      <div className="order-summary__items">
        {priceList
          .filter(item => item.is_visible && !['authorized_amount'].includes(item.key_name))
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((item, idx) => (
            <div
              key={idx}
              className={`order-summary__item ${
                item.key_name.includes('discount') || item.key_name.includes('coupon') 
                  ? 'order-summary__item--discount' 
                  : ''
              } ${
                item.key_name === 'grand_total' ? 'order-summary__item--total' : ''
              }`}
              style={item.style || {}}
            >
              <span>{item.title}</span>
              <span>${item.amount}</span>
            </div>
          ))}

        {/* Authorized Amount */}
        {priceList.find(p => p.key_name === 'authorized_amount') && (
          <div className="order-summary__item order-summary__item--authorized">
            <span>Authorized Amount</span>
            <span>${priceList.find(p => p.key_name === 'authorized_amount')?.amount}</span>
          </div>
        )}
      </div>

      {/* Coupons */}
      <div className="order-summary__coupons">
        <h4 className="order-summary__coupon-title">Applied Coupons</h4>
        {mockCoupons.map(coupon => (
          <label key={coupon.coupon_value} className="order-summary__coupon-item">
            <input
              type="checkbox"
              checked={selectedCoupons.includes(coupon.coupon_value)}
              onChange={() => onCouponToggle(coupon.coupon_value)}
            />
            <span>{coupon.coupon_title}</span>
          </label>
        ))}
      </div>

      {/* Notes */}
      <div className="order-summary__notes">
        ℹ️ {mockCartInfo.authorized_notes}
      </div>
    </div>
  );
};

export default CheckoutWizardMockup;
